import { memo, useEffect, useState } from 'react';
import css from './ImageFallback.module.scss';
import Fallback from '../../Assets/Images/FailedToLoadImage.png';
import classNames from 'classnames';
import type { ImageFallbackProps } from './ImageFallback.type';
import type { GoMotoComponent } from '../../API/GoMoto.types';

export const GMImageFallback: GoMotoComponent<ImageFallbackProps> = memo((props) => {
    const fallbackSrc: string = props.fallbackSrc ?? Fallback;
    const [resolvedSrc, setResolvedSrc] = useState<string>(fallbackSrc);

    useEffect(() => {
        let cancelled: boolean = false;
        const candidate: string = props.src;

        /**
         * If there is a candidate for an image to load, preload it and determine if
         * a valid image exists. If it does, we switch to the valid image. If it does not
         * we continue to show the current fallback image.
         *
         * Cancelled is so we don't try to display an image if the screen this component is mounted
         * in unmounts.
         */
        if (candidate) {
            const loader: HTMLImageElement = new Image();

            loader.onload = (): void => {
                if (!cancelled) {
                    setResolvedSrc(candidate);
                }
            };

            loader.onerror = (): void => {
                if (!cancelled) {
                    setResolvedSrc(fallbackSrc);
                }
            };

            loader.src = candidate;
        }
        else {
            setResolvedSrc(fallbackSrc);
        }

        return () => {
            cancelled = true;
        };
    }, [props.src, fallbackSrc]);

    return (
        <img
            src={resolvedSrc}
            alt={props.altText ?? ''}
            className={classNames(css.root, props.className)}
            style={props.style}
        />
    );
});